import { useState, useEffect, createContext } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Button, Space, message, Spin } from "antd";
import { SaveOutlined } from "@ant-design/icons";

import { MappingFieldsProvider } from "../contexts/MappingFields";
import {
  getBlueprintByUuid,
  createBlueprint,
  updateBlueprint,
} from "../apis/erpSyncBlueprint";
import { defaultBlueprint, cleanBlueprint } from "../utils/blueprint";
import SettingSteps from "../components/SpecSetting/SettingSteps";
import ConnectionStep from "../components/SpecSetting/Steps/1.ConnectionStep";
import FieldMappingStep from "../components/SpecSetting/Steps/2.FieldMappingStep";
import SyncRuleStep from "../components/SpecSetting/Steps/3.SyncRuleStep";
import UpdateRuleStep from "../components/SpecSetting/Steps/4.UpdateRuleStep";
import MemoStep from "../components/SpecSetting/Steps/5.MemoStep";

import "./SpecSetting.css";

export const SpecSettingContext = createContext();

const SpecSetting = () => {
  const { uuid } = useParams();
  const history = useHistory();
  const location = useLocation();
  const blueprintFromState =
    location.state?.["stateBlueprint"][uuid]["blueprint"];
  const approval = location.state?.["stateBlueprint"]["approval"];

  const [currentStep, setCurrentStep] = useState(0);
  const [stepLoading, setStepLoading] = useState(false);
  const [blueprint, setBlueprint] = useState(null);
  const [loading, setLoading] = useState(false);

  const steps = [
    {
      title: "連線配置",
      description: "設定 ERP 系統連線方式",
      component: ConnectionStep,
    },
    {
      title: "欄位映射",
      description: "配置欄位對應關係",
      component: FieldMappingStep,
    },
    {
      title: "同步規則",
      description: "設定新工單同步規則",
      component: SyncRuleStep,
    },
    {
      title: "更新規則",
      description: "設定現有工單更新規則",
      component: UpdateRuleStep,
    },
    {
      title: "藍圖筆記",
      description: "藍圖更動的筆記",
      component: MemoStep,
    },
  ];

  const CurrentStepComponent = steps[currentStep]["component"];

  const updateSettingBlueprint = (section, config) => {
    if (!blueprint) return;

    setBlueprint({
      ...blueprint,
      [section]: config,
    });
  };

  async function handleSave() {
    if (!blueprint) return;

    setLoading(true);
    try {
      const updatedBlueprint = cleanBlueprint(blueprint);
      setBlueprint(updatedBlueprint);

      await createBlueprint({
        blueprint: updatedBlueprint,
        status: 0,
      });

      message.success("Blueprint 保存成功");
      history.push("/history");
    } catch (error) {
      console.error("Error in handleSave: ", error);
      message.error("保存失敗");
    } finally {
      setLoading(false);
    }
  }

  async function handleApproval(status) {
    try {
      if (uuid) {
        await updateBlueprint(uuid, { status: status });
        message.success("更新狀態成功");
        history.push("/history");
      } else {
        message.error("更新狀態失敗");
        console.error("Error in handleApproval: No uuid found!");
      }
    } catch (error) {
      console.error("Error in handleApproval: ", error);
      message.error("更新狀態失敗");
    }
  }

  async function handleStepChange(newStep) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setStepLoading(true);
    await new Promise(resolve => setTimeout(resolve, 0));

    setCurrentStep(newStep);
    setTimeout(() => setStepLoading(false), 400);
  }

  useEffect(() => {
    if (blueprintFromState) {
      //// Use the blueprint data passed from SpecHistory
      setBlueprint(blueprintFromState);
    } else if (uuid && !blueprintFromState) {
      //// Fetch the blueprint data using the uuid as fallback
      getBlueprintByUuid(uuid)
        .then((response) => {
          setBlueprint(response["data"]["blueprint"]);
        })
        .catch((error) => {
          message.error("Failed to load blueprint");
          console.error("Error loading blueprint:", error);
        });
    } else {
      setBlueprint(defaultBlueprint);
    }
  }, [uuid, blueprintFromState]);

  return !blueprint ? (
    <Spin />
  ) : (
    <SpecSettingContext.Provider
      value={{
        steps,
        currentStep,
        setCurrentStep,
        blueprint,
        updateSettingBlueprint,
        handleStepChange,
      }}
    >
      <MappingFieldsProvider>
        <div className="spec-setting">
          <div className="spec-setting-steps">
            <SettingSteps />
          </div>

          <div className="spec-setting-content" style={{ position: "relative" }}>
            {stepLoading ? (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  background: "rgba(0, 0, 0, 0.3)",
                  zIndex: 999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Spin size="large" />
              </div>
            ) : null}

            <div className="content-header">{steps[currentStep]["title"]}</div>
            <CurrentStepComponent approval={approval} />

            <div style={{ textAlign: "center", marginTop: 24 }}>
              <Space size="large">
                {currentStep > 0 && (
                  <Button
                    type="primary"
                    onClick={() => handleStepChange(currentStep - 1)}
                  >
                    上一步
                  </Button>
                )}
                {currentStep < steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={() => handleStepChange(currentStep + 1)}
                  >
                    下一步
                  </Button>
                )}
                {currentStep === steps.length - 1 ? (
                  approval ? (
                    <>
                      <Button
                        danger
                        type="primary"
                        onClick={() => handleApproval(1)}
                      >
                        審核不通過
                      </Button>
                      <Button type="primary" onClick={() => handleApproval(2)}>
                        審核通過
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={handleSave}
                      loading={loading}
                    >
                      完成配置
                    </Button>
                  )
                ) : null}
              </Space>
            </div>
          </div>
        </div>
      </MappingFieldsProvider>
    </SpecSettingContext.Provider>
  );
};

export default SpecSetting;
